'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logger from '@/libs/Logger';
import type { SessionResponse, SessionUser, User } from '@/types/auth';
import { emitAuthEvent, onAuthEvent } from '@/utils/authEvents';
import { getLocalUuid } from '@/utils/getLocalUuid';
import { bindPersonToUser } from '@/utils/leadsService';
import { AuthContext } from './AuthContext';

const HEARTBEAT_INTERVAL_MS = 10 * 60 * 1000;
const SESSION_CACHE_TTL_MS = 30_000; // D8
const SESSION_NULL_CACHE_TTL_MS = 5000; // transient-failure backoff (critique #7i)
const SESSION_401_RETRY_DELAY_MS = 1500; // rotation-race grace (critique #1ii)

const EMPTY_USER: User = {
  id: '',
  name: '',
  givenName: '',
  email: '',
  phone: '',
  extra: { id: '', roles: [], permissions: [] },
};

// Single network probe. null => indeterminate (network/5xx): transient,
// never logout. {authenticated:false} => 401 (either middleware
// reason-401 or the session route's definitive 401).
async function requestSession(): Promise<SessionResponse | null> {
  try {
    // no-store: NextResponse.json sets no cache headers and the heartbeat
    // must never read a heuristically-cached response (critique #7ii).
    const res = await fetch('/api/auth/session', { cache: 'no-store' });
    if (res.status === 401) {
      return { authenticated: false };
    }
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as SessionResponse;
  } catch {
    return null;
  }
}

function buildUserFromSession(sessionUser: SessionUser): User {
  const { residentId, sub, name, givenName, email, phone, roles, permissions } =
    sessionUser;
  const user: User = {
    id: residentId ?? '',
    name,
    givenName,
    email,
    phone,
    extra: {
      id: sub,
      roles,
      permissions,
    },
  };

  logger.info('Setting user in Datadog', { user });
  const datadogUserInfo = {
    id: user.id,
    name,
    email,
    role: roles?.[0] ?? 'unknown',
  };
  datadogRum.setUser(datadogUserInfo);
  datadogLogs.setUser(datadogUserInfo);

  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('set', { user_id: sub });
  }

  return user;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<string | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const sessionCacheRef = useRef<{
    fetchedAt: number;
    session: SessionResponse | null;
  } | null>(null);
  const inFlightSessionRef = useRef<Promise<SessionResponse | null> | null>(
    null
  );

  const fetchSession = useCallback(
    async (force = false): Promise<SessionResponse | null> => {
      const cached = sessionCacheRef.current;
      if (!force && cached) {
        const ttl =
          cached.session === null
            ? SESSION_NULL_CACHE_TTL_MS
            : SESSION_CACHE_TTL_MS;
        if (Date.now() - cached.fetchedAt < ttl) {
          return cached.session;
        }
      }
      if (inFlightSessionRef.current) {
        return inFlightSessionRef.current;
      }
      inFlightSessionRef.current = (async () => {
        try {
          let session = await requestSession();
          if (session && !session.authenticated) {
            // Possible refresh-rotation race (see middleware B4): give the
            // winning refresh one RTT to land its cookies, then re-check
            // ONCE before caching a dead session -- a cached spurious
            // `false` would otherwise poison every getUserInfo consumer
            // (e.g. PaymentProvider's module-global user data) until
            // reload (critique #1ii).
            await new Promise((resolve) =>
              setTimeout(resolve, SESSION_401_RETRY_DELAY_MS)
            );
            session = await requestSession();
          }
          sessionCacheRef.current = { fetchedAt: Date.now(), session };
          return session;
        } finally {
          inFlightSessionRef.current = null;
        }
      })();
      return await inFlightSessionRef.current;
    },
    []
  );

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const logout = useCallback(async () => {
    logger.info('User logout initiated');
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      logger.error('Logout API call failed', { error: err });
    } finally {
      datadogRum.clearUser();
      datadogLogs.clearUser();

      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('set', { user_id: null });
      }

      stopHeartbeat();
      sessionCacheRef.current = null;
      window.location.href = '/login';
    }
  }, [stopHeartbeat]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }

    heartbeatRef.current = setInterval(async () => {
      logger.info('Executing session heartbeat');
      const s = await fetchSession(true);
      if (s && !s.authenticated) {
        logger.warn('Session heartbeat unauthenticated, logging out');
        await logout();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }, [fetchSession, logout]);

  const login = useCallback(async (identifier: string) => {
    try {
      setError(null);
      logger.info('Logging in', { identifier });
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      logger.info('Login response', { status: response.status });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to login');
        return false;
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to login';
      setError(message);
      return false;
    }
  }, []);

  const verifyOtp = useCallback(
    async (identifier: string, otp: string) => {
      try {
        setError(null);
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ identifier, otp }),
        });

        const data = await response.json();

        if (!response.ok) {
          setError(data.error || 'Failed to verify OTP');
          return false;
        }

        if (!(data.authenticated === true && data.user)) {
          logger.error('verifyOtp: Invalid session response', {
            authenticated: data?.authenticated,
          });
          setError('Invalid session response from server');
          return false;
        }

        // Seed the session cache from the login response so the very next
        // getUserInfo/heartbeat call doesn't have to round-trip.
        sessionCacheRef.current = { fetchedAt: Date.now(), session: data };
        startHeartbeat();

        // CLAUDE-16: bind the now-authenticated SFR3 user identity to the
        // leads-service Person row, threading the GA4 `local_uuid` baton
        // so server-side events can stitch cross-device sessions.
        // Fire-and-forget -- never block the resident-portal OTP flow on an
        // analytics-stitching call. Defensive `.catch` is
        // belt-and-suspenders; the helper already swallows internally.
        const { sub, phone } = data.user as SessionUser;
        const localUuid = getLocalUuid();
        if (sub && phone && localUuid) {
          bindPersonToUser(phone, sub, localUuid).catch(() => {
            // swallowed -- see helper
          });
        }

        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to verify OTP';
        setError(message);
        return false;
      }
    },
    [startHeartbeat]
  );

  const updateUserData = useCallback(async (userData: Partial<User>) => {
    logger.info('Attempting to update user data', { userId: userData.id });
    try {
      const response = await fetch('/api/auth/user', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        logger.error(
          'Failed to update user data: API returned non-OK response',
          {
            status: response.status,
            userId: userData.id,
          }
        );
        return;
      }

      const data = await response.json();
      logger.info('Successfully updated user data', { userId: userData.id });
      return data;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update user data';
      logger.error('Update user data failed with an exception', {
        error: err,
        userId: userData.id,
      });
      setError(message);
      throw err;
    }
  }, []);

  // s === null means the session probe itself failed transiently (network
  // error / 5xx / JWKS outage) -- NOT a confirmed logged-out state. Throwing
  // here (rather than silently returning EMPTY_USER) lets callers that
  // already retry-on-error (e.g. PaymentProvider's hasUserDataFetched reset
  // in its catch block) actually get a retry instead of caching a false
  // logged-out result until a full reload (critique #7i/#1ii).
  const getUserInfo = useCallback(async (): Promise<User> => {
    const s = await fetchSession();
    if (s === null) {
      throw new Error('Session check failed transiently');
    }
    return s.authenticated ? buildUserFromSession(s.user) : EMPTY_USER;
  }, [fetchSession]);

  // Resolves the mount-time session, retrying once if the first probe was
  // transient (see requestSession's null-means-indeterminate contract) so a
  // brief 503/JWKS blip doesn't permanently skip startHeartbeat() for an
  // otherwise-valid session (critique #1ii). Returns null if the effect was
  // cancelled mid-flight, so the caller never acts on a stale result.
  const resolveInitialSession = useCallback(
    async (
      isCancelled: () => boolean
    ): Promise<{
      session: SessionResponse | null;
      wasTransient: boolean;
    } | null> => {
      const first = await fetchSession();
      if (isCancelled()) {
        return null;
      }
      if (first !== null) {
        return { session: first, wasTransient: false };
      }
      await new Promise((resolve) =>
        setTimeout(resolve, SESSION_401_RETRY_DELAY_MS)
      );
      if (isCancelled()) {
        return null;
      }
      const retried = await fetchSession(true);
      return { session: retried, wasTransient: true };
    },
    [fetchSession]
  );

  useEffect(() => {
    let cancelled = false;

    const initialAuth = async () => {
      const result = await resolveInitialSession(() => cancelled);
      if (!result?.session?.authenticated) {
        // CRITICAL: a 401 on mount (e.g. login page) does NOT call logout();
        // only the heartbeat of an established session does.
        return;
      }

      // Restores mount-time Datadog RUM/Logs + gtag identify.
      buildUserFromSession(result.session.user);
      startHeartbeat();

      if (result.wasTransient) {
        // getUserInfo() throws on a transient session-check failure (see
        // getUserInfo above), so any consumer that called it once on mount
        // (e.g. LaunchDarklyProvider's UserContextUpdater) would have
        // caught that and settled on anonymous/default with no later
        // retrigger. Tell them a session was established after our own
        // retry so they can re-run their one-shot getUserInfo() call.
        emitAuthEvent('session-established');
      }
    };

    initialAuth();

    return () => {
      cancelled = true;
    };
  }, [resolveInitialSession, startHeartbeat]);

  useEffect(() => {
    return () => {
      stopHeartbeat();
    };
  }, [stopHeartbeat]);

  // Listen for unauthorized events from API services and trigger logout
  useEffect(() => {
    const unsubscribe = onAuthEvent('unauthorized', () => {
      logger.warn('Received unauthorized event, forcing logout');
      logout();
    });

    return unsubscribe;
  }, [logout]);

  const contextValue = useMemo(
    () => ({
      error,
      login,
      verifyOtp,
      logout,
      updateUserData,
      getUserInfo,
    }),
    [error, login, verifyOtp, logout, updateUserData, getUserInfo]
  );

  return <AuthContext value={contextValue}>{children}</AuthContext>;
}
